import {action, makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {ILeagues, ILeaguesStore} from "data/stores/leagues/leagues.store";
import {ModalType, RequestState} from "data/enums";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {AxiosError} from "axios";
import {IApiResponse} from "data/services/http";
import {extractErrorMessage} from "data/utils";
import {isEqual, find} from "lodash";
import {useNavigate} from "react-router-dom";

interface IProps {
	code?: string;
	navigate: ReturnType<typeof useNavigate>;
}

export interface IJoinLeaguesController extends ViewController<IProps> {
	get i18n(): ILocalizationStore;

	get leagues(): ILeagues;
	get isLoading(): boolean;

	loadMoreMyLeagues: () => void;
	joinLeague: (id: number) => void;
	isJoinInProgress: (id: number) => boolean;
}

@injectable()
export class JoinLeaguesController implements IJoinLeaguesController {
	@observable _requestState = RequestState.PENDING;
	@observable _joiningLeagueId?: number;
	@observable _navigate!: ReturnType<typeof useNavigate>;

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.LeaguesStore) private _leaguesStore: ILeaguesStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get leagues() {
		return this._leaguesStore.leaguesForJoin;
	}

	get isLoading() {
		return isEqual(this._requestState, RequestState.PENDING);
	}

	loadMoreMyLeagues = () => {
		this._leaguesStore.fetchMoreLeaguesForJoin().then(this.onSuccess).catch(this.onError);
	};

	@action private onSuccess = () => {
		this._requestState = RequestState.SUCCESS;
	};

	@action private onError = (error: AxiosError<IApiResponse>) => {
		this._requestState = RequestState.ERROR;
		this._modalsStore.showModal(ModalType.ERROR, {
			message: extractErrorMessage(error),
		});
	};

	@action joinLeague = (id: number) => {
		const code = find(this.leagues.leagues, {id})?.code;

		if (!code) return;

		this._requestState = RequestState.PENDING;
		this._joiningLeagueId = id;

		this._leaguesStore
			.joinToLeague({code})
			.then(() => this.redirectToMyLeagueByCode({code}))
			.catch(this.onError);
	};

	isJoinInProgress = (id: number) => {
		return this._joiningLeagueId === id;
	};

	private redirectToMyLeagueByCode = ({
		code,
		replace = false,
	}: {
		code: string;
		replace?: boolean;
	}) => {
		const {leagues} = this._leaguesStore.myLeagues;
		const id = find(leagues, {code})?.id;
		if (id) this._navigate(`/league/${id}/`, {replace});
	};

	private onShowJoinedModal = () => {
		setTimeout(() => {
			this._modalsStore.showModal(ModalType.LEAGUE_JOINED, {
				title: this.i18n.t("modal.league_joined.title", "CONGRATULATIONS"),
				message: this.i18n.t(
					"modal.league_joined.description",
					"You have now joined this league. Good luck!"
				),
			});
		}, 1000);
	};

	@action init({code, navigate}: IProps) {
		this._navigate = navigate;

		this._leaguesStore.fetchLeaguesForJoin().then(this.onSuccess).catch(this.onError);

		if (code) {
			console.log("CODE---", code);
			this._leaguesStore
				.joinToLeague({code})
				.then(() =>
					this.redirectToMyLeagueByCode({
						code,
						replace: true,
					})
				)
				.then(this.onShowJoinedModal)
				.catch(this.onError);
		}
	}
}
